import { Injectable } from '@angular/core';
import { LanguageService } from './language.service';

export interface ErrorMessages {
  en: string;
  hi: string;
}

@Injectable({
  providedIn: 'root'
})
export class TranslationService {

  constructor(private languageService: LanguageService) { }

  private readonly errorMessages: { [key: string]: ErrorMessages } = {
    // Connection Errors
    'connection_error': {
      en: 'Unable to connect to the server. Please check your internet connection.',
      hi: 'सर्वर से कनेक्ट नहीं हो सका। कृपया अपना इंटरनेट कनेक्शन जांचें।'
    },
    'connection_error_title': {
      en: 'Connection Error',
      hi: 'कनेक्शन त्रुटि'
    },

    // Authentication Errors
    'unauthorized_error': {
      en: 'Your session has expired. Please login again.',
      hi: 'आपका सत्र समाप्त हो गया है। कृपया पुनः लॉगिन करें।'
    },
    'unauthorized_title': {
      en: 'Unauthorized',
      hi: 'अनधिकृत'
    },

    // Authorization Errors
    'forbidden_error': {
      en: 'You do not have permission to perform this action.',
      hi: 'आपको यह कार्य करने की अनुमति नहीं है।'
    },
    'forbidden_title': {
      en: 'Access Denied',
      hi: 'पहुंच अस्वीकृत'
    },

    // Bad Request Errors
    'bad_request_error': {
      en: 'Invalid request. Please check your input.',
      hi: 'अमान्य अनुरोध। कृपया अपना इनपुट जांचें।'
    },
    'bad_request_title': {
      en: 'Bad Request',
      hi: 'गलत अनुरोध'
    },

    // Not Found Errors
    'not_found_error': {
      en: 'The requested resource was not found.',
      hi: 'अनुरोधित संसाधन नहीं मिला।'
    },
    'not_found_title': {
      en: 'Not Found',
      hi: 'नहीं मिला'
    },

    // Server Errors
    'server_error': {
      en: 'Internal server error. Please try again later.',
      hi: 'आंतरिक सर्वर त्रुटि। कृपया बाद में पुनः प्रयास करें।'
    },
    'server_error_title': {
      en: 'Server Error',
      hi: 'सर्वर त्रुटि'
    },

    // Service Unavailable
    'service_unavailable_error': {
      en: 'The server is temporarily unavailable. Please try again later.',
      hi: 'सर्वर अस्थायी रूप से अनुपलब्ध है। कृपया बाद में पुनः प्रयास करें।'
    },
    'service_unavailable_title': {
      en: 'Service Unavailable',
      hi: 'सेवा अनुपलब्ध'
    },

    // Validation Errors
    'validation_error': {
      en: 'Validation failed. Please check your input.',
      hi: 'सत्यापन असफल। कृपया अपना इनपुट जांचें।'
    },
    'validation_error_title': {
      en: 'Validation Error',
      hi: 'सत्यापन त्रुटि'
    },
    'validation_form_error': {
      en: 'Please correct the highlighted fields and try again.',
      hi: 'कृपया हाइलाइट किए गए फ़ील्ड को ठीक करें और पुनः प्रयास करें।'
    },

    // Generic Errors
    'generic_error': {
      en: 'An unexpected error occurred',
      hi: 'एक अप्रत्याशित त्रुटि हुई'
    },
    'error_title': {
      en: 'Error',
      hi: 'त्रुटि'
    },

    // Success Messages
    'success_title': {
      en: 'Success',
      hi: 'सफलता'
    },
    'operation_success': {
      en: 'Operation completed successfully!',
      hi: 'ऑपरेशन सफलतापूर्वक पूरा हुआ!'
    },

    // Warning Messages
    'warning_title': {
      en: 'Warning',
      hi: 'चेतावनी'
    },

    // Confirmation Messages
    'confirm_title': {
      en: 'Are you sure?',
      hi: 'क्या आप निश्चित हैं?'
    },
    'confirm_delete': {
      en: 'This action cannot be undone',
      hi: 'यह कार्य पूर्ववत नहीं हो सकता'
    },
    'confirm_yes': {
      en: 'Yes, proceed',
      hi: 'हां, आगे बढ़ें'
    },
    'confirm_cancel': {
      en: 'Cancel',
      hi: 'रद्द करें'
    },

    // Button Labels
    'ok_button': {
      en: 'OK',
      hi: 'ठीक है'
    },

    // Draft Status Messages
    'draft_error': {
      en: 'Form is in draft status. Please finalize the form before proceeding.',
      hi: 'फॉर्म ड्राफ्ट स्थिति में है। आगे बढ़ने से पहले कृपया फॉर्म को अंतिम रूप दें।'
    },
    'draft_title': {
      en: 'Draft Status', 
      hi: 'ड्राफ्ट स्थिति'
    }
  };

  /**
   * Get translated message based on current language
   */
  getMessage(key: string, fallback?: string): string {
    const messages = this.errorMessages[key];
    if (!messages) {
      return fallback || key;
    }

    const currentLang = this.languageService.currentLang;
    return messages[currentLang] || messages.en || fallback || key;
  }

  /**
   * Get translated messages object for a key
   */
  getMessages(key: string): ErrorMessages | null {
    return this.errorMessages[key] || null;
  }

  /**
   * Add or update translation messages
   */
  setMessages(key: string, messages: ErrorMessages): void {
    this.errorMessages[key] = messages;
  }
}